import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, Archive } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

const Choice = ({ active, onClick, icon: Icon, label, testid, disabled }) => (
  <button type="button" disabled={disabled} onClick={onClick} data-testid={testid}
    className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold border transition-colors disabled:opacity-40 ${active ? "bg-[#24474A] text-white border-[#24474A]" : "bg-white text-slate-600 border-slate-200 hover:border-[#24474A]/40"}`}>
    <Icon className="w-3.5 h-3.5" /> {label}
  </button>
);

export const CompleteProjectDialog = ({ open, project, onClose, onDone }) => {
  const open_tasks = project.tasks.filter((t) => t.status !== "done");
  const [makeLoc, setMakeLoc] = useState(true);
  const [name, setName] = useState(project.name);
  const [moves, setMoves] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setMakeLoc(true);
      setName(project.name);
      setMoves(Object.fromEntries(project.tasks.filter((t) => t.status !== "done").map((t) => [t.id, true])));
    }
  }, [open, project]);

  const setAll = (v) => setMoves(Object.fromEntries(open_tasks.map((t) => [t.id, v])));
  const submit = async () => {
    setBusy(true);
    try {
      const move_task_ids = makeLoc ? open_tasks.filter((t) => moves[t.id]).map((t) => t.id) : [];
      const { data } = await api.post(`/projects/${project.id}/complete`, { create_location: makeLoc, location_name: name, move_task_ids });
      toast.success(makeLoc ? `Project archived. Location "${name}" created with ${move_task_ids.length} task(s)` : "Project archived");
      onDone(data.location_id);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="complete-project-dialog">
        <DialogHeader><DialogTitle className="text-2xl text-[#24474A]">Complete project</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">The project will be archived and become read-only. Its tasks leave everyone's task lists and calendar.</p>
        <label className="flex items-start gap-3 rounded-xl border border-slate-200 p-3 cursor-pointer">
          <div className="flex-1"><div className="text-sm font-bold text-slate-800">Turn into a location for ongoing work</div><div className="text-xs text-slate-500">Use it to assign future tasks for this site.</div></div>
          <Switch checked={makeLoc} onCheckedChange={setMakeLoc} data-testid="complete-make-location-switch" />
        </label>
        {makeLoc && <div className="space-y-1.5"><Label>Location name</Label><Input value={name} onChange={(e) => setName(e.target.value)} data-testid="complete-location-name-input" /></div>}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>Unfinished tasks ({open_tasks.length})</Label>
            {open_tasks.length > 0 && makeLoc && (
              <div className="flex gap-1">
                <Choice icon={MapPin} label="All to location" onClick={() => setAll(true)} testid="complete-all-move" />
                <Choice icon={Archive} label="Archive all" onClick={() => setAll(false)} testid="complete-all-archive" />
              </div>
            )}
          </div>
          <div className="rounded-xl border border-slate-200 divide-y divide-slate-100" data-testid="complete-tasks-list">
            {open_tasks.map((t) => (
              <div key={t.id} className="flex items-center gap-2 px-3 py-2" data-testid={`complete-task-${t.id}`}>
                <div className="flex-1 min-w-0"><div className="text-sm font-bold text-slate-800 truncate">{t.title}</div><div className="text-xs text-slate-500">{t.assignee_name}</div></div>
                <Choice icon={MapPin} label="Location" disabled={!makeLoc} active={makeLoc && moves[t.id]} onClick={() => setMoves({ ...moves, [t.id]: true })} testid={`complete-move-${t.id}`} />
                <Choice icon={Archive} label="Archive" active={!makeLoc || !moves[t.id]} onClick={() => setMoves({ ...moves, [t.id]: false })} testid={`complete-archive-${t.id}`} />
              </div>
            ))}
            {open_tasks.length === 0 && <div className="px-3 py-4 text-sm text-slate-500 text-center">All tasks are done.</div>}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" className="rounded-full" onClick={onClose}>Cancel</Button>
          <Button className="rounded-full" onClick={submit} disabled={busy || (makeLoc && !name.trim())} data-testid="complete-project-confirm-btn">Complete & archive</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
